import axios from "axios";
import { requestActivity } from "@/services/requestActivity";

// const url = "http://localhost:1000";
const url = "https://server.facturize.net/vinech";

const fileUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${url}/${String(path).replace(/^\/+/, "")}`;
};

const api = axios.create({
  baseURL: url+"/index.php",
  timeout: 10000,
  headers: {
    Accept: "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("facturize_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const currentUser = JSON.parse(localStorage.getItem("facturize_user") || "null");
  const establishmentId = localStorage.getItem("facturize_establishment_id");
  if (String(currentUser?.nivel || "").toLowerCase() === "manager" && establishmentId && establishmentId !== "all") {
    config.headers["X-Establishment-Id"] = establishmentId;
  }
  const endpoint = String(config.url || "").split("?")[0];
  if (
    String(config.method || "get").toLowerCase() === "post" &&
    endpoint === "/documentos" &&
    !navigator.onLine &&
    config.data &&
    typeof config.data === "object"
  ) {
    config.data.emissao_offline = true;
  }
  config._activityId = requestActivity.start(config);
  return config;
});

const findAgtError = (data) => {
  if (!data || typeof data !== "object") return null;
  const agt = data.agtResponse || data.details?.agtResponse || data.detalhes?.agtResponse;
  const list = agt?.errorList || data.errorList || data.details?.errorList;
  const first = Array.isArray(list) ? list.find(Boolean) : null;
  if (first) {
    const code = first.idError || first.code || "AGT";
    const description = first.descriptionError || first.message || "A AGT recusou o pedido.";
    return `${code}: ${description}`;
  }
  const raw = [data.error, data.message, data.detail]
    .filter(Boolean)
    .join(" ");
  return /\bAGT\b|\bE\d{2,4}\b|seriesFEResult/i.test(raw) ? raw : null;
};

const diagnoseError = (error, fallbackMessage) => {
  const status = Number(error.response?.status || 0);
  const data = error.response?.data;
  const agtError = findAgtError(data);

  if (agtError) {
    return {
      category: "agt",
      title: "Pedido recusado pela AGT",
      message: agtError,
    };
  }
  if (!navigator.onLine) {
    return {
      category: "network",
      title: "Sem ligação à internet",
      message: "O computador perdeu a ligação. Confirme a rede e tente novamente; o erro não foi devolvido pela AGT.",
    };
  }
  if (error.code === "ECONNABORTED" || /timeout/i.test(String(error.message || ""))) {
    return {
      category: "timeout",
      title: "O pedido demorou demasiado",
      message: "Não houve resposta dentro do tempo esperado. Pode existir lentidão na internet, no servidor Facturize ou no serviço da AGT.",
    };
  }
  if (!error.response) {
    return {
      category: "network",
      title: "Servidor inacessível",
      message: "Não foi possível chegar ao servidor Facturize. Este erro ocorreu antes de existir uma resposta da AGT.",
    };
  }
  if ([502, 503, 504].includes(status)) {
    return {
      category: "server",
      title: "Serviço externo indisponível",
      message: fallbackMessage,
    };
  }
  return {
    category: "application",
    title: status >= 500 ? "Erro no servidor Facturize" : "Não foi possível concluir",
    message: fallbackMessage,
  };
};

api.interceptors.response.use(
  (response) => {
    requestActivity.finish(response.config?._activityId);
    return response;
  },
  (error) => {
    const endpoint = String(error.config?.url || "pedido").split("?")[0];
    const timedOut = error.code === "ECONNABORTED";
    const status = Number(error.response?.status || 0);
    const serverData = error.response?.data;
    const serverMessage =
      (serverData && typeof serverData === "object" &&
        (serverData.error || serverData.message || serverData.detail)) ||
      (typeof serverData === "string" && !serverData.trim().startsWith("<")
        ? serverData.trim()
        : "");
    const reference =
      serverData && typeof serverData === "object"
        ? serverData.request_id || serverData.reference
        : null;
    const statusLabels = {
      400: "Pedido inválido",
      401: "Sessão expirada",
      403: "Acesso não autorizado",
      404: "Recurso não encontrado",
      409: "Conflito de dados",
      422: "Dados não aceites",
      423: "Licença bloqueada",
      429: "Muitos pedidos",
      500: "Erro interno do servidor",
      502: "Serviço externo indisponível",
      503: "Serviço temporariamente indisponível",
    };
    const message = timedOut
      ? `O pedido ${endpoint} excedeu o tempo limite. Verifique a ligação e tente novamente.`
      : !error.response
      ? `Não foi possível comunicar com o servidor ao executar ${endpoint}. Verifique a internet e tente novamente.`
      : `${statusLabels[status] || `Erro HTTP ${status}`}: ${
          serverMessage || "O servidor não forneceu detalhes adicionais."
        }${reference ? ` Referência: ${reference}.` : ""}`;

    error.userMessage = message;
    error.diagnosis = diagnoseError(error, message);
    const isPageRead =
      String(error.config?.method || "get").toLowerCase() === "get";
    requestActivity.fail(error.config?._activityId, error.diagnosis, !isPageRead);
    if (!error.response) error.response = { status: 0, data: {} };
    if (!error.response.data || typeof error.response.data !== "object")
      error.response.data = {};
    error.response.data.error = message;

    if (status === 0 && !isPageRead) {
      localStorage.setItem(
        "facturize_connection_error",
        message
      );
    }
    const isLicenseBlocked =
      (status === 423 &&
        ["LICENSE_EXPIRED", "LICENSE_SUSPENDED", "LICENSE_MISSING", "LICENSE_INVALID", "LICENSE_REQUIRED"].includes(serverData?.code)) ||
      (status === 503 && serverData?.code === "LICENSE_UNAVAILABLE");
    if (
      isLicenseBlocked &&
      !window.location.hash.includes("/empresa/licenciamento")
    ) {
      window.location.hash = "#/empresa/licenciamento";
    }
    if (
      status === 401 &&
      !window.location.hash.includes("/auth/login")
    ) {
      localStorage.removeItem("facturize_token");
      localStorage.removeItem("facturize_user");
      if (!window.location.hash.includes("/auth/login"))
        window.location.hash = "#/auth/login";
    }
    if (
      status === 403 &&
      !window.location.hash.includes("/erro/403")
    ) {
      window.location.hash = "#/erro/403";
    }
    if (status >= 500 && !isPageRead) {
      localStorage.setItem(
        "facturize_last_server_error",
        message
      );
    }
    return Promise.reject(error);
  }
);

export { api, url, fileUrl };
export { diagnoseError };
